/**
 * Definitions PARTAGEES entre l'ecran Reservations et son module de reglages
 * (settings.tsx) : catalogue des pastilles de filtre, catalogue des stats
 * d'en-tete, cles de prefs et valeurs par defaut. Un seul endroit, zero
 * duplication.
 *
 * Les prefs sont locales au poste (localStorage via `usePref` de @taki/ui) :
 *  - "app.reservations.filters"     : Record<filterId, boolean> (visibilite des pastilles) ;
 *  - "app.reservations.stats"       : string[] (ids ordonnes des stats, max 3) ;
 *  - "app.reservations.defaultDays" : number (duree de location par defaut, en jours).
 */

/** Ids des pastilles de filtre de la zone 4. Elles RAFFINENT la section du menu. */
export type ReservationFilterId = 'toutes' | 'option' | 'confirmee' | 'sortie';

/**
 * Pastilles de la zone 4. Leur VISIBILITE est editable dans settings.tsx ; les
 * valeurs, elles, sont le catalogue FIGE des statuts du pack.
 */
export const FILTER_DEFS: ReadonlyArray<{ id: ReservationFilterId; label: string }> = [
  { id: 'toutes', label: 'Toutes' },
  { id: 'option', label: 'Option' },
  { id: 'confirmee', label: 'Confirmee' },
  { id: 'sortie', label: 'Sortie' },
];

/** Pastille par defaut : celle sur laquelle on retombe si l'active est masquee. */
export const DEFAULT_FILTER_ID: ReservationFilterId = 'toutes';

/** Cle de pref : visibilite des pastilles. Cle absente = pastille visible. */
export const FILTERS_PREF_KEY = 'app.reservations.filters';

/** Defaut filtres : toutes les pastilles visibles. */
export const DEFAULT_FILTERS: Record<string, boolean> = Object.fromEntries(
  FILTER_DEFS.map((def) => [def.id, true]),
);

/** Ids des statistiques disponibles pour l'annexe d'en-tete (zone 3). */
export type ReservationStatId = 'total' | 'a-venir' | 'en-cours' | 'en-retard' | 'ca-mois';

/**
 * Catalogue des stats affichables en haut a droite. La SELECTION (max 3) est
 * editable dans settings.tsx ; l'ordre d'affichage suit ce catalogue et les
 * valeurs restent calculees des donnees reelles par l'ecran.
 */
export const STAT_DEFS: ReadonlyArray<{ id: ReservationStatId; label: string }> = [
  // `total` compte TOUTES les locations, annulee comprise : c'est un total.
  // La section Planning, elle, exclut les annulees ; l'ecart d'une ligne entre
  // le compteur du menu et cette stat est donc normal, et voulu.
  { id: 'total', label: 'locations' },
  { id: 'a-venir', label: 'a venir' },
  // `en-cours` = materiel dehors ET dans les temps ; un retard n'est compte que
  // dans `en-retard`. Les deux chiffres ne se recouvrent jamais.
  { id: 'en-cours', label: 'en cours' },
  { id: 'en-retard', label: 'en retard' },
  { id: 'ca-mois', label: 'CA du mois' },
];

/** Nombre maximal de stats affichees (contrat de la cle "app.<id>.stats"). */
export const STATS_MAX = 3;

/** Cle de pref : ids ordonnes des stats affichees. */
export const STATS_PREF_KEY = 'app.reservations.stats';

/** Defaut stats : le tableau de bord du comptoir (total, en cours, en retard). */
export const DEFAULT_STATS: string[] = ['total', 'en-cours', 'en-retard'];

/**
 * Cle de pref METIER : duree de location par defaut, en jours. Elle pre-remplit
 * la date de retour du formulaire de creation, rien d'autre : aucune donnee en
 * base ne depend d'elle.
 */
export const DEFAULT_DAYS_PREF_KEY = 'app.reservations.defaultDays';

/** Duree de location par defaut, en jours. */
export const DEFAULT_DAYS = 3;

/** Bornes acceptees par le reglage de duree (garde-fou de saisie). */
export const DEFAULT_DAYS_MIN = 1;
export const DEFAULT_DAYS_MAX = 90;
